import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { ImageOverlay } from "@/components/editor/types";
import { animationTemplates } from "@/components/editor/templates/animation-templates";
import { Img } from "remotion";

/**
 * Props for the ImageLayerContent component
 * @interface ImageLayerContentProps
 * @property {ImageOverlay} overlay - The image overlay object containing source and style information
 */
interface ImageLayerContentProps {
  overlay: ImageOverlay;
}

/**
 * ImageLayerContent Component
 *
 * @component
 * @description
 * Renders an image layer in the video editor with animation support.
 * Features include:
 * - Enter/exit animations
 * - Style customization (fit, position, opacity)
 * - Transform effects
 * - Visual effects (filters, shadows, borders)
 *
 * The component handles both the visual presentation and animation
 * timing for image overlays.
 *
 * @example
 * ```tsx
 * <ImageLayerContent
 *   overlay={{
 *     src: "path/to/image.jpg",
 *     styles: {
 *       objectFit: "cover",
 *       animation: {
 *         enter: "fadeIn",
 *         exit: "fadeOut"
 *       }
 *     }
 *   }}
 * />
 * ```
 */
export const ImageLayerContent: React.FC<ImageLayerContentProps> = ({
  overlay,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const isExitPhase = frame >= overlay.durationInFrames - 30;

  /**
   * Apply enter animation only during entry phase
   */
  const enterAnimation =
      !isExitPhase && overlay.styles.animation?.enter
        ? animationTemplates[overlay.styles.animation?.enter?.name ?? ""]?.enter?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.enter
          )
        : {};

  /**
   * Apply loop animation only during entry phase
   */
    const loopAnimation = animationTemplates[overlay.styles.animation?.loop?.name ?? ""]?.loop?.(
      frame,
      overlay.durationInFrames,
      fps,
      overlay.styles.animation?.loop
    )
  ?? {};

  /**
   * Apply exit animation only during exit phase
   */
  const exitAnimation =
      isExitPhase && overlay.styles.animation?.exit
        ? animationTemplates[overlay.styles.animation?.exit?.name ?? ""]?.exit?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.exit
          )
        : {};

  /**
   * Combine base styles with current animation phase
   */
  const imageStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: overlay.styles.objectFit || "cover",
    objectPosition: overlay.styles.objectPosition,
    opacity: overlay.styles.opacity,
    transform: overlay.styles.transform || "none",
    filter: overlay.styles.filter || "none",
    borderRadius: overlay.styles.borderRadius || "0px",
    boxShadow: overlay.styles.boxShadow || "none",
    border: overlay.styles.border || "none",
    ...(isExitPhase ? exitAnimation : enterAnimation),
    ...loopAnimation
  };

  return <Img src={overlay.src} style={imageStyle} alt="" />;
};
